import React from 'react';
import { useNavigate } from 'react-router-dom';
import './DataEngineeringPage.css';

const DataEngineeringPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'Business Intelligence',
      icon: '📊',
      description: 'Transform raw data into actionable insights with powerful BI solutions.',
      features: [
        'Interactive dashboards and reports',
        'Real-time data visualization',
        'KPI tracking and monitoring',
        'Self-service analytics',
        'Mobile BI solutions',
        'Embedded analytics'
      ]
    },
    {
      title: 'Data Analytics',
      icon: '🔍',
      description: 'Deep dive into your data to uncover trends, patterns, and opportunities.',
      features: [
        'Descriptive and diagnostic analytics',
        'Predictive modeling',
        'Statistical analysis',
        'Customer segment analysis',
        'A/B testing and experimentation',
        'Performance optimization'
      ]
    },
    {
      title: 'Data Pipeline Solutions',
      icon: '⚙️',
      description: 'Build robust, scalable data pipelines for seamless data flow.',
      features: [
        'Automated data ingestion',
        'Real-time streaming pipelines',
        'Batch processing workflows',
        'Data orchestration',
        'Data quality validation',
        'Pipeline monitoring and alerts'
      ]
    },
    {
      title: 'ETL Processing',
      icon: '🔄',
      description: 'Extract, transform, and load data efficiently across systems.',
      features: [
        'Data extraction from multiple sources',
        'Complex data transformations',
        'Data cleansing and normalization',
        'Incremental and full loads',
        'Change data capture (CDC)',
        'Error handling and recovery'
      ]
    }
  ];

  const technologies = [
    { name: 'Python', category: 'Programming' },
    { name: 'Apache Spark', category: 'Big Data' },
    { name: 'Apache Airflow', category: 'Orchestration' },
    { name: 'dbt', category: 'Transformation' },
    { name: 'Snowflake', category: 'Data Warehouse' },
    { name: 'BigQuery', category: 'Data Warehouse' },
    { name: 'Redshift', category: 'Data Warehouse' },
    { name: 'Tableau', category: 'BI Tools' },
    { name: 'Power BI', category: 'BI Tools' },
    { name: 'Kafka', category: 'Streaming' },
    { name: 'Databricks', category: 'Platform' },
    { name: 'PostgreSQL', category: 'Database' }
  ];

  const benefits = [
    {
      title: 'Data-Driven Decisions',
      description: 'Make informed decisions backed by accurate data',
      icon: '🎯'
    },
    {
      title: 'Operational Efficiency',
      description: 'Automate processes and reduce manual work',
      icon: '⚡'
    },
    {
      title: 'Scalable Infrastructure',
      description: 'Handle growing data volumes with ease',
      icon: '📈'
    },
    {
      title: 'Cost Optimization',
      description: 'Reduce infrastructure and operational costs',
      icon: '💰'
    }
  ];

  const useCases = [
    {
      title: 'Customer Analytics',
      description: 'Understand customer behavior, preferences, and lifetime value',
      icon: '👥',
      examples: [
        'Customer segmentation',
        'Churn prediction',
        'Personalization engines',
        'Customer journey analysis'
      ]
    },
    {
      title: 'Sales & Revenue',
      description: 'Track performance and forecast future revenue',
      icon: '💼',
      examples: [
        'Sales forecasting',
        'Revenue attribution',
        'Pipeline analytics',
        'Pricing optimization'
      ]
    },
    {
      title: 'Operational Analytics',
      description: 'Monitor and optimize business operations',
      icon: '⚙️',
      examples: [
        'Process efficiency',
        'Resource utilization',
        'Inventory management',
        'Supply chain analytics'
      ]
    },
    {
      title: 'Financial Reporting',
      description: 'Automated financial dashboards and compliance',
      icon: '📈',
      examples: [
        'P&L statements',
        'Budget vs actuals',
        'Cash flow analysis',
        'Financial forecasting'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Data Assessment',
      description: 'Evaluate current data landscape, sources, and quality issues'
    },
    {
      step: '02',
      title: 'Architecture Design',
      description: 'Design scalable data architecture aligned with business needs'
    },
    {
      step: '03',
      title: 'Pipeline Development',
      description: 'Build and test data pipelines with proper governance'
    },
    {
      step: '04',
      title: 'Analytics & BI',
      description: 'Create dashboards, reports, and analytical models'
    },
    {
      step: '05',
      title: 'Monitoring & Support',
      description: 'Continuous monitoring, optimization, and support'
    }
  ];

  const dataStack = [
    {
      layer: 'Data Sources',
      components: ['Databases', 'APIs', 'SaaS Apps', 'Files', 'Streams']
    },
    {
      layer: 'Ingestion',
      components: ['Fivetran', 'Airbyte', 'Custom Connectors', 'Kafka']
    },
    {
      layer: 'Storage',
      components: ['Snowflake', 'BigQuery', 'Redshift', 'Data Lakes']
    },
    {
      layer: 'Transformation',
      components: ['dbt', 'Spark', 'SQL', 'Python']
    },
    {
      layer: 'Orchestration',
      components: ['Airflow', 'Dagster', 'Prefect']
    },
    {
      layer: 'Visualization',
      components: ['Tableau', 'Power BI', 'Looker', 'Metabase']
    }
  ];

  const metrics = [
    { value: '99.9%', label: 'Pipeline Uptime' },
    { value: '10x', label: 'Query Performance' },
    { value: '50%', label: 'Cost Reduction' },
    { value: '<1hr', label: 'Data Freshness' }
  ];

  return (
    <div className="data-engineering-page">
      {/* Hero Section */}
      <section className="data-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="hero-content">
            <h1 className="hero-title">
              Data Engineering Solutions
            </h1>
            <p className="hero-subtitle">
              Transform your data chaos into clarity with robust pipelines, analytics, and insights that drive business growth
            </p>
            <div className="hero-stats">
              <div className="stat">
                <h3>500TB+</h3>
                <p>Data Processed</p>
              </div>
              <div className="stat">
                <h3>100+</h3>
                <p>Pipelines Built</p>
              </div>
              <div className="stat">
                <h3>99.9%</h3>
                <p>Pipeline Reliability</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail">
        <div className="container">
          <h2 className="section-title">Our Data Services</h2>
          <p className="section-subtitle">
            End-to-end data engineering and analytics solutions
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-detail-card">
                <div className="service-icon-large">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="technologies">
        <div className="container">
          <h2 className="section-title">Our Technology Expertise</h2>
          <p className="section-subtitle">
            Modern data stack for enterprise-grade solutions
          </p>
          <div className="tech-grid">
            {technologies.map((tech, index) => (
              <div key={index} className="tech-item">
                <span className="tech-name">{tech.name}</span>
                <span className="tech-category">{tech.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="use-cases">
        <div className="container">
          <h2 className="section-title">Real-World Applications</h2>
          <p className="section-subtitle">
            How we help businesses leverage their data
          </p>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <div className="use-case-icon">{useCase.icon}</div>
                <h3>{useCase.title}</h3>
                <p className="use-case-desc">{useCase.description}</p>
                <ul className="examples-list">
                  {useCase.examples.map((example, idx) => (
                    <li key={idx}>{example}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Data Stack */}
      <section className="data-stack">
        <div className="container">
          <h2 className="section-title">Modern Data Stack</h2>
          <p className="section-subtitle">
            Our comprehensive approach to data infrastructure
          </p>
          <div className="stack-layers">
            {dataStack.map((layer, index) => (
              <div key={index} className="stack-layer">
                <div className="layer-name">{layer.layer}</div>
                <div className="layer-components">
                  {layer.components.map((component, idx) => (
                    <span key={idx} className="component-tag">{component}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title">Why Choose Our Solutions</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <h2 className="section-title">Our Methodology</h2>
          <p className="section-subtitle">
            Proven approach to data engineering success
          </p>
          <div className="process-timeline">
            {process.map((item, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Performance Metrics */}
      <section className="performance-metrics">
        <div className="container">
          <h2 className="section-title">Performance Guarantees</h2>
          <p className="section-subtitle">
            Industry-leading reliability and performance
          </p>
          <div className="metrics-grid">
            {metrics.map((metric, index) => (
              <div key={index} className="metric-card">
                <div className="metric-value">{metric.value}</div>
                <div className="metric-label">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Unlock Your Data's Potential?</h2>
            <p>Let's build a data infrastructure that scales with your ambitions</p>
            <div className="cta-buttons">
              <button className="primary-btn" onClick={() => navigate('/#contact')}>
                Schedule a Consultation
              </button>
              <button className="secondary-btn" onClick={() => navigate('/')}>
                Explore More Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataEngineeringPage;
